
import { getAuthToken } from "@/utils/authUtils";
import { safeJsonParse } from "./utils";
import {
  StreamState,
  handleStartEvent,
  handleInitialSummaryEvent,
  handleProductUpdateEvent,
  handleStatusEvent,
  handleItemStartEvent,
  handleItemUpdateEvent,
  handleTooManyResultsEvent,
  handleCompleteEvent
} from "./eventHandlers";
import { SearchResult } from "@/types/inventory";

interface SSEConnectionConfig {
  searchTerm: string;
  state: StreamState;
  onUpdate: (result: SearchResult) => void;
  onComplete: (finalResult: SearchResult) => void;
  onFallback: () => Promise<void>;
}

export const setupSSEConnection = (config: SSEConnectionConfig): EventSource => {
  const { searchTerm, state, onUpdate, onComplete, onFallback } = config;
  
  // Get auth token for SSE connection
  const token = getAuthToken();
  
  // Build SSE URL with appropriate authentication
  let sseUrl = `https://DABS-scraper.onrender.com/api/search/stream?searchTerm=${encodeURIComponent(searchTerm)}`;
  if (token) {
    sseUrl += `&token=${token}`;
    console.log(`🔌 [DEBUG] Opening SSE connection as authenticated user`);
  } else {
    sseUrl += `&guest=true`;
    console.log(`🔌 [DEBUG] Opening SSE connection as guest user`);
  }
  
  console.log(`🔌 [DEBUG] Opening SSE connection to: ${sseUrl}`);
  const eventSource = new EventSource(sseUrl);
  
  let hasReceivedData = false;
  
  // Set a timeout for SSE connection
  const sseTimeout = setTimeout(() => {
    console.log("⏰ [DEBUG] SSE timeout reached, closing connection");
    eventSource.close();
    if (!hasReceivedData) {
      console.log("📡 [DEBUG] No data received via SSE, falling back to traditional search");
      onFallback();
    }
  }, 10000); // 10 second timeout
  
  eventSource.onopen = () => {
    console.log("✅ [DEBUG] SSE connection opened successfully");
    clearTimeout(sseTimeout);
  };
  
  // Event listeners with safe JSON parsing
  eventSource.addEventListener('start', (event: MessageEvent) => {
    hasReceivedData = true;
    const data = safeJsonParse(event.data, 'start');
    if (data) {
      handleStartEvent(data, state);
    }
  });
  
  eventSource.addEventListener('initial-summary', (event: MessageEvent) => {
    hasReceivedData = true;
    const data = safeJsonParse(event.data, 'initial-summary');
    if (data) {
      handleInitialSummaryEvent(data, state, onUpdate);
    }
  });
  
  eventSource.addEventListener('product-update', (event: MessageEvent) => {
    const data = safeJsonParse(event.data, 'product-update');
    if (data) {
      hasReceivedData = true;
      handleProductUpdateEvent(data, state, onUpdate);
    }
  });
  
  eventSource.addEventListener('status', (event: MessageEvent) => {
    const data = safeJsonParse(event.data, 'status');
    if (data) {
      hasReceivedData = true;
      handleStatusEvent(data, state, onUpdate);
    }
  });
  
  eventSource.addEventListener('item-start', (event: MessageEvent) => {
    const data = safeJsonParse(event.data, 'item-start');
    if (data) {
      hasReceivedData = true;
      handleItemStartEvent(data, state, onUpdate);
    }
  });
  
  eventSource.addEventListener('item-update', (event: MessageEvent) => {
    const data = safeJsonParse(event.data, 'item-update');
    if (data) {
      hasReceivedData = true;
      handleItemUpdateEvent(data, state, onUpdate);
    }
  });
  
  // Keep existing products event handler for backward compatibility
  eventSource.addEventListener('products', (event: MessageEvent) => {
    hasReceivedData = true;
    const data = safeJsonParse(event.data, 'products');
    if (!data) return;
    
    console.log("📦 [DEBUG] Products event received (legacy):", data);
    
    state.hasReceivedProducts = true;
    
    // Check for too_many_results flag in products event
    if (data.too_many_results) {
      console.log("🚫 [DEBUG] Too many results detected in products event");
      state.currentResult = {
        searchTerm,
        items: [],
        timestamp: new Date().toISOString(),
        too_many_results: true,
        totalItemsFromAPI: data.totalItemsFromAPI || data.count || 0,
        maxAllowedItems: data.maxAllowedItems || data.threshold || 10
      };
      
      onUpdate(state.currentResult);
      eventSource.close();
      onComplete(state.currentResult);
      return;
    }
    
    if (!data.items || data.items.length === 0) {
      console.log("⚠️ [DEBUG] Products event has no items or empty items array");
      return;
    }
    
    // Clear any placeholder items
    state.itemsMap.clear();
    
    // Process items from products event (legacy path)
    data.items.forEach((item: any, i: number) => {
      const inventoryItem = {
        id: item.sku || `${searchTerm.toLowerCase().replace(/\s+/g, "-")}-${i}`,
        name: item.name,
        category: "Whiskey",
        price: item.price || "$0.00",
        size: "750 ML",
        storeQty: 0,
        warehouseQty: parseInt(item.warehouseQty) || 0,
        onOrderQty: parseInt(item.onOrderQty) || 0,
        location: "Various Utah Stores",
        lastChecked: new Date().toISOString(),
        stores: item.stores || []
      };
      
      // Calculate store quantity if stores data exists
      if (inventoryItem.stores.length > 0) {
        inventoryItem.storeQty = inventoryItem.stores.reduce((total: number, store: any) => total + store.qty, 0);
      }
      
      if (item.sku) {
        state.itemsMap.set(item.sku, inventoryItem);
      }
    });
    
    state.currentResult = {
      searchTerm,
      items: Array.from(state.itemsMap.values()),
      timestamp: new Date().toISOString()
    };
    
    console.log(`📦 [DEBUG] Updating UI with ${state.currentResult.items.length} items from products event`);
    onUpdate(state.currentResult);
  });
  
  // Handle batch events
  eventSource.addEventListener('batch-start', (event: MessageEvent) => {
    const data = safeJsonParse(event.data, 'batch-start');
    if (data) {
      console.log("🔄 [DEBUG] Batch start event:", data.batchNumber, "of", data.totalBatches);
    }
  });
  
  eventSource.addEventListener('batch-complete', (event: MessageEvent) => {
    const data = safeJsonParse(event.data, 'batch-complete');
    if (data) {
      console.log("✅ [DEBUG] Batch complete event:", data.batchNumber, "of", data.totalBatches);
    }
  });
  
  // Handle too-many-results event
  eventSource.addEventListener('too-many-results', (event: MessageEvent) => {
    hasReceivedData = true;
    const data = safeJsonParse(event.data, 'too-many-results');
    if (data) {
      handleTooManyResultsEvent(data, state, onUpdate, onComplete);
      eventSource.close();
    }
  });
  
  // Handle refinement_required event
  eventSource.addEventListener('refinement_required', (event: MessageEvent) => {
    hasReceivedData = true;
    const data = safeJsonParse(event.data, 'refinement_required');
    if (data) {
      console.log("🔍 [DEBUG] Refinement required event:", data);
      handleTooManyResultsEvent(data, state, onUpdate, onComplete);
      eventSource.close();
    }
  });
  
  // Handle complete event
  eventSource.addEventListener('complete', (event: MessageEvent) => {
    const data = safeJsonParse(event.data, 'complete');
    if (data) {
      handleCompleteEvent(data, state, onUpdate, onComplete);
      eventSource.close();
    }
  });
  
  // Handle error events
  eventSource.addEventListener('error', (event: MessageEvent) => {
    const data = safeJsonParse(event.data, 'error');
    if (!data) return;
    
    console.error("❌ [DEBUG] SSE Error event:", data);
    
    // Check if this is a too-many-results error
    if (data.message && data.message.includes('too many results')) {
      hasReceivedData = true;
      console.log("🚫 [DEBUG] Error event contains 'too many results' message");
      handleTooManyResultsEvent(data, state, onUpdate, onComplete);
      eventSource.close();
      return;
    }
    
    eventSource.close();
    onFallback();
  });
  
  eventSource.onerror = (error) => {
    console.error("🔥 [DEBUG] SSE connection error:", error);
    eventSource.close();
    if (!hasReceivedData) {
      onFallback();
    } else {
      console.log(`🔥 [DEBUG] Error occurred but we have ${state.currentResult.items.length} items, completing`);
      onComplete(state.currentResult);
    }
  };
  
  return eventSource;
};
